import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import TimelineIcon from "@mui/icons-material/Timeline";

const WorkflowModal = ({ open, onClose, data, onSave }) => {
  const isEdit = !!data;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    trigger: "Webhook",
    triggerConfig: {
      url: "",
      method: "POST",
      schedule: "",
    },
    actions: [],
    status: "Active",
  });

  const [newAction, setNewAction] = useState({
    type: "Call",
    config: "",
  });

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        description: data.description || "",
        trigger: data.trigger || "Webhook",
        triggerConfig: data.triggerConfig || {
          url: "",
          method: "POST",
          schedule: "",
        },
        actions: data.actions || [],
        status: data.status || "Active",
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleTriggerConfigChange = (field, value) => {
    setFormData({
      ...formData,
      triggerConfig: {
        ...formData.triggerConfig,
        [field]: value,
      },
    });
  };

  const handleAddAction = () => {
    if (newAction.type && newAction.config) {
      setFormData({
        ...formData,
        actions: [...formData.actions, { ...newAction }],
      });
      setNewAction({ type: "Call", config: "" });
    }
  };

  const handleRemoveAction = (index) => {
    setFormData({
      ...formData,
      actions: formData.actions.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = () => {
    onSave(formData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      trigger: "Webhook",
      triggerConfig: {
        url: "",
        method: "POST",
        schedule: "",
      },
      actions: [],
      status: "Active",
    });
    setNewAction({ type: "Call", config: "" });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" fontWeight={600}>
            {isEdit ? "Edit Workflow" : "Create Workflow"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Basic Information */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Workflow Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Workflow Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                placeholder="e.g., Welcome Call Campaign"
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                multiline
                rows={2}
                placeholder="Brief description of the workflow"
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleChange("status", e.target.value)}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Paused">Paused</MenuItem>
                  <MenuItem value="Testing">Testing</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider />

          {/* Trigger Configuration */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Trigger Configuration
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Trigger Type</InputLabel>
                <Select
                  value={formData.trigger}
                  label="Trigger Type"
                  onChange={(e) => handleChange("trigger", e.target.value)}
                >
                  <MenuItem value="Webhook">Webhook</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="New Customer">New Customer</MenuItem>
                  <MenuItem value="Payment Due">Payment Due</MenuItem>
                  <MenuItem value="Service Complete">Service Complete</MenuItem>
                  <MenuItem value="Order Placed">Order Placed</MenuItem>
                </Select>
              </FormControl>

              {formData.trigger === "Webhook" && (
                <>
                  <TextField
                    fullWidth
                    label="Webhook URL"
                    value={formData.triggerConfig.url}
                    onChange={(e) => handleTriggerConfigChange("url", e.target.value)}
                    placeholder="https://api.example.com/webhook"
                  />
                  <FormControl fullWidth>
                    <InputLabel>HTTP Method</InputLabel>
                    <Select
                      value={formData.triggerConfig.method}
                      label="HTTP Method"
                      onChange={(e) => handleTriggerConfigChange("method", e.target.value)}
                    >
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="POST">POST</MenuItem>
                      <MenuItem value="PUT">PUT</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}

              {formData.trigger === "Scheduled" && (
                <TextField
                  fullWidth
                  label="Schedule (Cron Expression)"
                  value={formData.triggerConfig.schedule}
                  onChange={(e) => handleTriggerConfigChange("schedule", e.target.value)}
                  placeholder="0 9 * * * (Every day at 9 AM)"
                  helperText="Use cron expression format"
                />
              )}
            </Box>
          </Box>

          <Divider />

          {/* Actions */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Workflow Actions
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Define the sequence of actions to perform when this workflow is triggered
            </Typography>

            <Card sx={{ mt: 2, backgroundColor: "#F9FAFB" }}>
              <CardContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Action Type</InputLabel>
                      <Select
                        value={newAction.type}
                        label="Action Type"
                        onChange={(e) => setNewAction({ ...newAction, type: e.target.value })}
                        size="small"
                      >
                        <MenuItem value="Call">Make Phone Call</MenuItem>
                        <MenuItem value="SMS">Send SMS</MenuItem>
                        <MenuItem value="Email">Send Email</MenuItem>
                        <MenuItem value="API">Call API</MenuItem>
                        <MenuItem value="Wait">Wait/Delay</MenuItem>
                        <MenuItem value="Condition">Conditional Branch</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      size="small"
                      label="Configuration"
                      value={newAction.config}
                      onChange={(e) => setNewAction({ ...newAction, config: e.target.value })}
                      placeholder="e.g., +1 (555) 123-4567"
                    />
                  </Box>
                  <CustomButton
                    icon={<AddIcon />}
                    iconPosition="left"
                    text="Add Action"
                    onClick={handleAddAction}
                    disabled={!newAction.type || !newAction.config}
                    size="small"
                    fullWidth
                  />
                </Box>
              </CardContent>
            </Card>

            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
              {formData.actions.map((action, idx) => (
                <Card key={idx} sx={{ border: "1px solid #E0E0E0" }}>
                  <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Chip label={`Step ${idx + 1}`} size="small" color="primary" />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {action.type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {action.config}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton size="small" onClick={() => handleRemoveAction(idx)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </CardContent>
                </Card>
              ))}
              {formData.actions.length === 0 && (
                <Box sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>
                  <TimelineIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    No actions added yet. Add your first action above.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton text="Cancel" variant="outlined" onClick={handleClose} />
        <CustomButton
          icon={<SaveIcon />}
          iconPosition="left"
          text={isEdit ? "Update Workflow" : "Create Workflow"}
          onClick={handleSubmit}
          disabled={!formData.name.trim() || formData.actions.length === 0}
        />
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowModal;
